import React, { useState, useEffect, useRef } from 'react';
import { useCourt } from '../context/contextHooks';
import { useForm } from "react-hook-form";
import { isValid } from "date-fns";
import parse from 'html-react-parser';
import {getCaseLabel, getCitationLabel, getLicenseLabel,} from "./lookupFunctions";
import { useStyles as classes } from "../styles/styles";
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from '@mui/lab/TabPanel';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import FormLabel from "@mui/material/FormLabel";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InputAdornment from "@mui/material/InputAdornment";
import Popup from "../layout/Popup";
import Tooltip from '@mui/material/Tooltip';
import Zoom from "@mui/material/Zoom";
import Dob from './Dob';
import Link from "@mui/material/Link";
import Stack from '@mui/material/Stack';
import {PatternFormat} from "react-number-format";

const LookupForm = (props) => {
    const goBack = props.goBack;
    const findBalances = props.findBalances;
    const citationImage = props.citationImage;
    const caseImage = props.caseImage;
    const showRestore = props.showRestore;
    const restorePartyData = props.restorePartyData;
    const searchButtonRef = props.searchButtonRef;
    const { register, handleSubmit, setValue } = useForm();
    const alertRef = useRef(null);
    const [court,] = useCourt();
    const theme = useTheme();
    const smallHeader = useMediaQuery(theme.breakpoints.down('lg'));
    const caseLabel = getCaseLabel(court);
    const citationLabel = getCitationLabel(court);
    const licenseLabel = getLicenseLabel(court);
    const [tabVal, setTabVal] = useState('1');
    const [dob, setDob] = useState(null);
    const [openCaseInfo, setOpenCaseInfo] = useState(false);
    const [openCitationInfo, setOpenCitationInfo] = useState(false);
    const [openLicenseInfo,setOpenLicenseInfo] = useState(false);
    const [openGraphicInfo, setOpenGraphicInfo] = useState(false);
    const [openCaseGraphicInfo, setOpenCaseGraphicInfo] = useState(false);
    const [errorFieldId, setErrorFieldId] = useState(null);
    // id of the error Alert rendered by the parent (BalanceLookup); referenced by
    // aria-describedby on the field in error so the message is announced on focus.
    const ERROR_ID = 'lookup-error';
    // Only the field in error gets aria-describedby, so we never reference the
    // Alert id while it is absent from the page.
    const errorProps = (fieldId) => (errorFieldId === fieldId ? { 'aria-describedby': ERROR_ID } : {});
    const handleTabChange = (e, newValue) => {
        setTabVal(newValue);
        switch (newValue) {
            case '1':
                setValue('caseNo', null);
                setValue('licenseLookupNumber', null);
                setValue('firstName', null);
                setValue('lastName', null);
                break;
            case '2':
                setValue('citeNo', null);
                setValue('licenseLookupNumber', null);
                setValue('firstName', null);
                setValue('lastName', null);
                break;
            case '3':
                setValue('citeNo', null);
                setValue('caseNo', null);
                setValue('firstName', null);
                setValue('lastName', null);
                break
            case '4':
                setValue('citeNo', null);
                setValue('caseNo', null);
                setValue('licenseLookupNumber', null);
        }
    }

    // Identifies the first field in error for the active tab, mirroring the order
    // of validateLookup, so focus can be moved to it (WCAG 2.4.3).
    const firstErrorField = (form) => {
        switch (tabVal) {
            case '1':
                if (!form.citeNo) return 'citeNo';
                if (court.minCitationLength && form.citeNo.length < court.minCitationLength) return 'citeNo';
                break;
            case '2':
                if (!form.caseNo) return 'caseNo';
                if (court.minCaseLength && form.caseNo.length < court.minCaseLength) return 'caseNo';
                break;
            case '3':
                if (!form.licenseLookupNumber) return 'licenseLookupNumber';
                return null;
            case '4':
                if (!form.firstName) return 'firstName';
                if (!form.lastName) return 'lastName';
                break;
            default:
                break;
        }
        if (court.zipLookups) {
            if (!form.zip) return 'zip';
        } else if (!court.noDobLookup) {
            if (!dob || !isValid(new Date(dob))) return 'dob';
        }
        return null;
    };

    const handleSearch = (form) => {
        const errors = findBalances(form, dob);
        if (errors && errors.length > 0) {
            const fieldId = firstErrorField(form);
            setErrorFieldId(fieldId);
            if (fieldId) {
                // Defer to let the error Alert render before focus so the
                // aria-describedby target exists and is announced.
                setTimeout(() => {
                    const el = document.getElementById(fieldId);
                    if (el) el.focus();
                }, 0);
            }
        } else {
            setErrorFieldId(null);
        }
    }

    const closeCaseInfo = () => {
        setOpenCaseInfo(false);
    }

    const closeCitationInfo = () => {
        setOpenCitationInfo(false);
    }
    const closeLicenseInfo = () => {
        setOpenLicenseInfo(false);
    }
    const openCitationGraphic= () => {
        closeCaseGraphic();
        setOpenGraphicInfo(!openGraphicInfo);
    };

    const closeCitationGraphic = () => {
        setOpenGraphicInfo(false);
    };

    const openCaseGraphic = () => {
        closeCitationGraphic();
        setOpenCaseGraphicInfo(!openCaseGraphicInfo);
    };

    const closeCaseGraphic = () => {
        setOpenCaseGraphicInfo(false);
    };

    const handleKeyDown = (handler) => (e) => {
        if (e.key === 'Enter' || e.key === ' ' ) {
            e.preventDefault();
            handler();
        }
    };

    const handleInfoClick = (type, event) => {
        if (event.type === 'click' || (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' '))) {
            event.preventDefault();
            switch (type) {
                case 'citation':
                    setOpenCitationInfo(true);
                    break;
                case 'case':
                    setOpenCaseInfo(true);
                    break;
                case 'license':
                    setOpenLicenseInfo(true);
                    break;
                default:
                    break;
            }
        }
    };

    useEffect(() => {
        if (court && court.noCitationLookup) {
            setTabVal('2');
        } else setTabVal('1');
    }, [court])

    return (
        <>
            { openCaseInfo && court.caseNumberInstructions &&
                <Popup open={openCaseInfo} setDone={closeCaseInfo} title={`${caseLabel} Information`} component={<Typography component='div'>{parse(court.caseNumberInstructions)}</Typography>} />
            }
            { openCitationInfo && court.citationNotice &&
                <Popup open={openCitationInfo} setDone={closeCitationInfo} title={`${citationLabel} Information`} component={<Typography component='div'>{parse(court.citationNotice)}</Typography>} />
            }
                { openLicenseInfo &&
                <Popup
                    open={openLicenseInfo}
                    setDone={closeLicenseInfo}
                    title="License Plate Information"
                    component={
                        <Typography component='div'>Enter your license plate number with no spaces or dashes (do not enter the state).</Typography>
                    }
                />
            }
            {openGraphicInfo && <>
                <Popup open={true} title="Citation Number Example Information" setDone={closeCitationGraphic} component={
                    <Stack>
                        <Box component='img' src={window.URL.createObjectURL(citationImage)} alt='Sample citation showing citation number in top left corner.' sx={{margin: '3px .5rem'}}/>
                        <Typography variant="body2" sx={{textAlign: 'center', mt: 1}}>
                            The citation number is typically located in the top left corner of your citation. Please refer to the highlighted area in the sample image above.
                        </Typography>
                    </Stack>
                }/> </>
            }
            {openCaseGraphicInfo && <>
                <Popup open={true} title="Case Number Example Information" setDone={closeCaseGraphic} component={
                    <Stack>
                        <Box component='img' src={window.URL.createObjectURL(caseImage)} alt='Sample case document showing case number location.' sx={{margin: '3px .5rem'}}/>
                        <Typography variant="body2" sx={{textAlign: 'center', mt: 1}}>
                            The case number is typically found at the top of your case document. Please refer to the highlighted area in the sample image above.
                        </Typography>
                    </Stack>
                }/> </>
            }
            <Paper elevation={3} sx={classes.controlPanel}>
                { court.lookupNotice ?
                    <Typography sx={{fontSize: smallHeader ? '1rem' : '1.1rem'}}>{parse(court.lookupNotice)}</Typography> :
                    <Alert ref={alertRef} icon={false} severity='info' role="status" aria-live="polite" sx={{marginTop: '1rem', marginBottom: '.5rem'}}>
                        <Typography sx={{fontSize: smallHeader ? '1rem' : '1.1rem'}}>
                            Enter the {caseLabel} or {citationLabel} for a balance you wish to pay, but not both.
                            {court.noDobLookup ? '' : 'Your date of birth is required to find most balances.'}
                        </Typography>
                    </Alert>
                }
                <Typography variant={smallHeader ? 'subtitle1' : 'h6'} component='h2' color='primary'>Find Balances By:</Typography>
                <Box component='div' sx={{ width: '100%', typography: 'body1', fontSize: '.9rem' }}>
                    <TabContext value={tabVal} >
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList
                                value={tabVal}
                                onChange={handleTabChange}
                                variant='scrollable'
                                scrollButtons='auto'
                                aria-label="lookup tabs"
                            >
                                {!court.noCitationLookup &&
                                <Tab label={getCitationLabel(court)} value='1' wrapped={true}/>
                                }
                                <Tab label={getCaseLabel(court)} value='2' wrapped={true} />
                                { court.licensePlateLookup &&
                                    <Tab label={licenseLabel} value='3' wrapped={true}/>
                                }
                                { court.allowNameSearch &&
                                    <Tab label='Name' value='4' wrapped={true}/>
                                }
                            </TabList>
                        </Box>
                        {!court.noCitationLookup &&
                        <TabPanel value='1' sx={{padding: '1rem'}}>
                            <Grid container spacing={1} alignItems='top'>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={classes.formControl}>
                                        <FormLabel shrink required sx={classes.inputLabel} htmlFor='citeNo'>
                                            {citationLabel}
                                        </FormLabel>
                                        <TextField
                                            id='citeNo'
                                            variant='outlined'
                                            name="citeNo"
                                            fullWidth
                                            InputProps={ court.citationNotice && {
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Tooltip title={`${court.nonCourtOrganization ? 'Lookup' : 'Citation'} information`} TransitionComponent={Zoom} arrow>
                                                            <IconButton
                                                                aria-label={`${court.nonCourtOrganization ? 'Lookup' : 'Citation'} information`}
                                                                onClick={event => handleInfoClick('citation', event)}
                                                                onKeyDown={event => handleInfoClick('citation', event)}
                                                                sx={{ display: 'inline-flex', alignItems: 'center' }}
                                                                size="small"
                                                                tabIndex={0}
                                                            >
                                                                <InfoOutlinedIcon color='primary' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                            inputProps={{sx: classes.textbox, "aria-required": "true", ...errorProps('citeNo')}}
                                            {...register('citeNo')}
                                        />
                                    </FormControl>
                                </Grid>
                                {!court.noDobLookup && !court.zipLookups &&
                                    <Grid item xs={12} md={6}>
                                        <Dob dob={dob} setDob={setDob} required={!court.noDobLookup} errorId={errorFieldId === 'dob' ? ERROR_ID : undefined} />
                                    </Grid>
                                }
                                {court.zipLookups &&
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={classes.formControl} {...register('zip')}>
                                            <FormLabel shrink required sx={classes.inputLabel} htmlFor='zip'>Zip Code</FormLabel>
                                            <PatternFormat
                                                id='zip'
                                                name='zip'
                                                customInput={TextField}
                                                type='text'
                                                autoComplete='off'
                                                variant='outlined'
                                                format='#####'
                                                InputProps={{
                                                    style: classes.numberbox
                                                }}
                                                inputProps={{
                                                    "aria-required": "true",
                                                    ...errorProps('zip')
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                }
                            </Grid>
                            {citationImage &&
                                <Grid item xs={10}><Typography><Button sx={{textTransform: 'none', padding: '0', fontSize: 'inherit', color: 'primary.main', justifyContent: 'flex-start'}} onClick={openCitationGraphic} onKeyDown={handleKeyDown(openCitationGraphic)}>Click to see {citationLabel} example</Button></Typography></Grid>
                            }
                        </TabPanel>
                        }
                        <TabPanel value='2' sx={{padding: '1rem'}}>
                            <Grid container spacing={1} alignItems='top'>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={classes.formControl}>
                                        <FormLabel shrink required sx={classes.inputLabel} htmlFor='caseNo'>
                                            {caseLabel}
                                        </FormLabel>
                                        <TextField
                                            variant='outlined'
                                            id='caseNo'
                                            name="caseNo"
                                            fullWidth
                                            InputProps={ court.caseNumberInstructions && {
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Tooltip title={`${court.nonCourtOrganization ? 'Account' : 'Case'} information`} TransitionComponent={Zoom} arrow>
                                                            <IconButton
                                                                aria-label={`${court.nonCourtOrganization ? 'Account' : 'Case'} information`}
                                                                onClick={event => handleInfoClick('case', event)}
                                                                onKeyDown={event => handleInfoClick('case', event)}
                                                                sx={{ display: 'inline-flex', alignItems: 'center' }}
                                                                size="small"
                                                                tabIndex={0}
                                                            >
                                                                <InfoOutlinedIcon color='primary' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                            inputProps={{sx: classes.textbox, "aria-required": "true", ...errorProps('caseNo')}}
                                            {...register('caseNo')}
                                        />
                                    </FormControl>
                                </Grid>
                                {!court.noDobLookup && !court.zipLookups &&
                                    <Grid item xs={12} md={6}>
                                        <Dob dob={dob} setDob={setDob} required={!court.noDobLookup} errorId={errorFieldId === 'dob' ? ERROR_ID : undefined} />
                                    </Grid>
                                }
                                {court.zipLookups &&
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={classes.formControl} {...register('zip')}>
                                            <FormLabel shrink required sx={classes.inputLabel} htmlFor='zip'>Zip Code</FormLabel>
                                            <PatternFormat
                                                id='zip'
                                                name='zip'
                                                customInput={TextField}
                                                type='text'
                                                autoComplete='off'
                                                variant='outlined'
                                                format='#####'
                                                InputProps={{
                                                    style: classes.numberbox
                                                }}
                                                inputProps={{
                                                    "aria-required": "true",
                                                    ...errorProps('zip')
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                }
                                {caseImage &&
                                    <Grid item xs={10}><Typography><Button sx={{textTransform: 'none', padding: '0', fontSize: 'inherit', color: 'primary.main', justifyContent: 'flex-start'}} onClick={openCaseGraphic} onKeyDown={handleKeyDown(openCaseGraphic)}>Click to see {caseLabel} example</Button></Typography></Grid>
                                }
                            </Grid>
                        </TabPanel>
                        { court.licensePlateLookup &&
                            <TabPanel value='3' sx={{padding: '1rem'}}>
                                <Grid container spacing={1} alignItems='top'>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={classes.formControl}>
                                            <FormLabel shrink={true} sx={classes.inputLabel} htmlFor='licenseLookupNumber'>
                                                {licenseLabel}
                                            </FormLabel>
                                            <TextField
                                                id='licenseLookupNumber'
                                                variant='outlined'
                                                name="licenseLookupNumber"
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position='end'>
                                                            <Tooltip title={`License Plate Information`} TransitionComponent={Zoom} arrow>
                                                                <IconButton
                                                                    aria-label="License Plate Information"
                                                                    onClick={event => handleInfoClick('license', event)}
                                                                    onKeyDown={event => handleInfoClick('license', event)}
                                                                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                                                                    size="small"
                                                                    tabIndex={0}
                                                                >
                                                                    <InfoOutlinedIcon color='primary' />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                inputProps={{sx: classes.textbox, "aria-required": "true", ...errorProps('licenseLookupNumber')}}
                                                {...register('licenseLookupNumber')}

                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </TabPanel>
                        }
                        { court.allowNameSearch &&
                            <TabPanel value='4' sx={{padding: '1rem'}}>
                                <Grid container spacing={1} alignItems='top'>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={classes.formControl} {...register('firstName')}>
                                            <FormLabel shrink={true} sx={classes.inputLabel} htmlFor='firstName'>
                                                First Name
                                            </FormLabel>
                                            <TextField
                                                id='firstName'
                                                variant='outlined'
                                                name="firstName"
                                                fullWidth
                                                inputProps={{sx: classes.textbox, "aria-required": "true", ...errorProps('firstName')}}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={classes.formControl} {...register('lastName')}>
                                            <FormLabel shrink={true} sx={classes.inputLabel} htmlFor='lastName'>
                                                Last Name
                                            </FormLabel>
                                            <TextField
                                                id='lastName'
                                                variant='outlined'
                                                name="lastName"
                                                fullWidth
                                                inputProps={{sx: classes.textbox, "aria-required": "true", ...errorProps('lastName')}}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Dob dob={dob} setDob={setDob} required={!court.noDobLookup} errorId={errorFieldId === 'dob' ? ERROR_ID : undefined} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant='subtitle2'>
                                            {`Enter your first and last name as they appear on the citation or court documents${!court.noDobLookup ? ', and your date of birth' : ''}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </TabPanel>
                        }
                    </TabContext>
                </Box>
                <Box component='div' sx={{...classes.root, display: 'flex', flexDirection: 'row-reverse', marginTop: '1rem'}} >
                    <Button
                        variant='contained'
                        color='primary'
                        startIcon={<ManageSearchIcon />}
                        sx={classes.squashedButton}
                        onClick={handleSubmit(handleSearch)}
                        ref={searchButtonRef}
                    >
                        Search
                    </Button>
                    { showRestore &&
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={<ManageSearchIcon />}
                            sx={classes.squashedButton}
                            onClick={() => restorePartyData()}
                        >
                            Restore Party Results
                        </Button>
                    }
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowBackIosNewRoundedIcon />}
                        sx={classes.squashedButton}
                        onClick={goBack}
                    >
                        Back
                    </Button>
                </Box>
            </Paper>
        </>
    );
}

export default LookupForm;
